import { ReactNode, useEffect, useRef } from "react";
import { useIonRouter } from "@/app/IonicContextProvider";
import { useAuthContext } from "@/lib/authContext";
import { Skeleton } from "@/shared/components/Skeletons";
import { authService } from "@/lib/auth";

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, isInitialized, setAuthenticated } = useAuthContext();
  const router = useIonRouter();
  const hasCheckedStorageRef = useRef(false);


  // Re-check storage if auth state is lost (safeguard against state desync)
  useEffect(() => {
    if (isInitialized && !isLoading && !isAuthenticated && !hasCheckedStorageRef.current) {
      hasCheckedStorageRef.current = true;
      
      // Double-check storage before redirecting
      authService.getRestoredUser().then((restoredUser) => {
        if (restoredUser) {
          setAuthenticated(restoredUser);
          hasCheckedStorageRef.current = false; // Reset for next check
        } else {
          router.push("/login", "root", "replace");
        }
      }).catch((error) => {
        console.error("AuthGuard: Error checking storage:", error);
        router.push("/login", "root", "replace");
      });
    } else if (isAuthenticated) {
      // Reset the check flag when authenticated
      hasCheckedStorageRef.current = false;
    }
  }, [isInitialized, isLoading, isAuthenticated, router, setAuthenticated]);

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
